import { useEffect, useState } from "react";
import { scoreSheets } from "../local/db";

/**
 * card #761 AC2 — a locked (not-mine) section stays read-only, but once the
 * ASSIGNED booth's own sheet for this ride has been submitted (completed),
 * this booth should read its values instead of showing a blank row.
 *
 * useScoreSheet's own fetch cycle already pulls (and caches — see its
 * fromApi(), `scoreSheets.putAll(sheets)`) EVERY judge's sheet for a ride
 * when online, since score-api's listScoreSheets isn't judgeId-filtered
 * server-side. This hook just reads that same local IndexedDB cache for a
 * DIFFERENT booth position — no separate network call, and none of
 * useScoreSheet's own-sheet edit/autosave machinery (this booth never
 * writes to another judge's sheet).
 *
 * Returns null until the other position's sheet exists AND is completed;
 * the caller (ScoringForm) only ever reads from this for a section that
 * isn't this booth's to edit.
 */
export function useOtherSectionSubmission(rideId, otherPosition, refreshKey) {
  const [otherSheet, setOtherSheet] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!rideId || !otherPosition) {
      setOtherSheet(null);
      return undefined;
    }
    (async () => {
      const rows = await scoreSheets.byRideJudge(rideId, otherPosition).catch(() => []);
      const found = rows.find((s) => s.judgeId === otherPosition && s.completedAt) ?? null;
      if (!cancelled) setOtherSheet(found);
    })();
    return () => {
      cancelled = true;
    };
  }, [rideId, otherPosition, refreshKey]);

  return otherSheet;
}
